import React, { useState, useEffect } from "react";
import { Dialog, DialogContent } from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  Fullscreen,
  FullscreenExit,
} from "@mui/icons-material";
import API_BASE_URL from "../../../../Constants";
import { useSelector } from "react-redux";
import { selectNavHeight } from "../../../../redux/navbar/navHeightSlice";

const DirectorEntryForm = ({
  open,
  onClose,
  refreshDirectors,
  directorData,
}) => {
  const [directorFormData, setDirectorFormData] = useState({
    name: "",
    email: "",
    password: "",
  });
  // State for reset password in edit mode
  const [resetPassword, setResetPassword] = useState("");
  const [isExpanded, setIsExpanded] = useState(false);
  const [errors, setErrors] = useState({});

  const navHeight = useSelector(selectNavHeight);

  // Populate form fields when `directorData` changes.
  // For edit mode, clear the password field.
  useEffect(() => {
    if (directorData) {
      setDirectorFormData({
        name: directorData.name || "",
        email: directorData.email || "",
        password: "", // Do not prefill password in edit mode
      });
      setResetPassword(""); // Clear any previous reset password
    } else {
      setDirectorFormData({
        name: "",
        email: "",
        password: "",
      });
    }
  }, [directorData]);

  // Validation logic for add/edit (excludes reset password field)
  const validateFields = () => {
    // Only require password if we're adding a new director
    const requiredFields = {
      name: "Name",
      email: "Email",
      ...(!directorData && { password: "Password" }),
    };

    const newErrors = {};
    Object.keys(requiredFields).forEach((field) => {
      if (!directorFormData[field]?.toString().trim()) {
        newErrors[field] = `${requiredFields[field]} is required.`;
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleFieldChange = (e) => {
    const { name, value } = e.target;
    setDirectorFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
    setErrors((prevErrors) => ({ ...prevErrors, [name]: "" }));
  };

  const renderError = (fieldKey) =>
    errors[fieldKey] ? (
      <p className="text-red-500 text-sm mt-1">{errors[fieldKey]}</p>
    ) : null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateFields()) return;

    // Build the payload: only include password if adding a new director.
    const payload = {
      name: directorFormData.name,
      email: directorFormData.email,
      ...(directorData ? {} : { password: directorFormData.password }),
    };

    try {
      const url = directorData
        ? `${API_BASE_URL}/api/setup/dobconnectdirectors/${directorData.id}`
        : `${API_BASE_URL}/api/setup/dobconnectdirectors/post`;
      const method = directorData ? "PUT" : "POST";

      const response = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(
          directorData
            ? "Failed to update director."
            : "Failed to create director."
        );
      }

      // Refresh the list in the parent
      if (refreshDirectors) refreshDirectors();
      onClose();
    } catch (err) {
      console.error(err);
      alert(
        directorData
          ? "An error occurred while updating the director."
          : "An error occurred while creating the director."
      );
    }
  };

  // Handle reset password for edit mode
  const handleResetPassword = async () => {
    if (!resetPassword.trim()) {
      alert("Please enter a new password to reset.");
      return;
    }
    try {
      // Call the reset password endpoint (using a placeholder for now)
      const response = await fetch(
        `${API_BASE_URL}/api/setup/dobconnectdirectors/${directorData.id}/reset-password`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ password: resetPassword }),
        }
      );
      if (!response.ok) {
        throw new Error("Password reset failed.");
      }
      alert("Password reset successfully.");
      setResetPassword(""); // Clear the input after success
    } catch (err) {
      console.error(err);
      alert("An error occurred while resetting the password.");
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={isExpanded ? false : "sm"}
      fullWidth
      PaperProps={{
        style: {
          position: "absolute",
          top: navHeight,
          left: "50%",
          transform: "translateX(-50%)",
          margin: 4,
          transition: "width 0.3s ease, height 0.3s ease",
          width: isExpanded ? "90%" : "1000px",
          height: `calc(100vh - ${navHeight + 8}px)`,
          overflow: "auto",
        },
      }}
    >
      <div className="sticky top-0 bg-white z-10">
        <div className="flex justify-between items-center p-4">
          <div className="flex items-center space-x-4 flex-grow">
            <button
              onClick={onClose}
              className="p-2 rounded-full hover:bg-gray-200 transition"
              aria-label="Back"
            >
              <ArrowBackIcon />
            </button>
            <h2 className="flex text-2xl font-bold ml-4">
              {directorData ? "Edit Director" : "Add Director"}
            </h2>
          </div>
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="p-2 rounded-full bg-white hover:bg-gray-200 transition"
            aria-label={isExpanded ? "Shrink Modal" : "Expand Modal"}
          >
            {isExpanded ? <FullscreenExit /> : <Fullscreen />}
          </button>
        </div>
      </div>

      <DialogContent dividers>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <label
              htmlFor="name"
              className="block text-sm font-bold text-gray-700"
            >
              Name
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={directorFormData.name}
              onChange={handleFieldChange}
              className="w-full px-3 py-2 text-sm leading-tight text-gray-700 border rounded shadow appearance-none focus:outline-none"
            />
            {renderError("name")}
          </div>

          <div className="space-y-2">
            <label
              htmlFor="email"
              className="block text-sm font-bold text-gray-700"
            >
              Email
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={directorFormData.email}
              onChange={handleFieldChange}
              className="w-full px-3 py-2 text-sm leading-tight text-gray-700 border rounded shadow appearance-none focus:outline-none"
            />
            {renderError("email")}
          </div>

          {/* Render password field only when adding a new director */}
          {!directorData && (
            <div className="space-y-2">
              <label
                htmlFor="password"
                className="block text-sm font-bold text-gray-700"
              >
                Password
              </label>
              <input
                type="password"
                id="password"
                name="password"
                value={directorFormData.password}
                onChange={handleFieldChange}
                className="w-full px-3 py-2 text-sm leading-tight text-gray-700 border rounded shadow appearance-none focus:outline-none"
              />
              {renderError("password")}
            </div>
          )}

          {/* Reset Password section for edit mode */}
          {directorData && (
            <div className="space-y-2 border-t pt-4">
              <label
                htmlFor="resetPassword"
                className="block text-sm font-bold text-gray-700"
              >
                Reset Password
              </label>
              <input
                type="password"
                id="resetPassword"
                name="resetPassword"
                value={resetPassword}
                onChange={(e) => setResetPassword(e.target.value)}
                placeholder="Enter new password"
                className="w-full px-3 py-2 text-sm leading-tight text-gray-700 border rounded shadow appearance-none focus:outline-none"
              />
              <button
                type="button"
                onClick={handleResetPassword}
                className="mt-2 px-4 py-2 bg-green-500 text-white font-bold rounded-md hover:bg-green-600 transition"
                disabled={!resetPassword.trim()}
              >
                Reset Password
              </button>
            </div>
          )}

          <div className="flex justify-end space-x-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-gray-300 font-bold rounded-md hover:bg-gray-400 transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-6 py-2 bg-blue-500 text-white font-bold rounded-md hover:bg-blue-600 transition"
            >
              Submit
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default DirectorEntryForm;
